/**
 * CommandPalette. The L3 glass surface for Ctrl+K / Cmd+K: an input on top,
 * results grouped under Label headers, keyboard first.
 *
 * Presentation only. The caller supplies the groups (static destinations and
 * actions, live search results, recents) and decides what each item does.
 *
 *   ArrowUp / ArrowDown   move the highlight (wraps)
 *   Home / End            first / last result
 *   Enter                 run the highlighted item
 *   Escape                close, focus returns to whatever opened it
 *   Tab                   stays inside the palette (focus is trapped)
 *
 * ARIA: the input is a combobox that owns a listbox, with the highlighted
 * option exposed through aria-activedescendant, so focus never leaves the
 * input while you move through results.
 */

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { CornerDownLeft, Search, type LucideIcon } from "lucide-react";
import {
  Fragment,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { Skeleton, SkeletonLine } from "@/primitives/ui/Skeleton";

import "./commandPalette.css";

export interface PaletteItem {
  id: string;
  label: string;
  sub?: string;
  icon: LucideIcon;
  /** Small trailing text, e.g. "Page" or "Action". */
  hint?: string;
  run: () => void;
}

export interface PaletteGroup {
  label: string;
  items: PaletteItem[];
  /** Show skeleton rows under this header while results load. */
  loading?: boolean;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  query: string;
  onQueryChange: (q: string) => void;
  groups: PaletteGroup[];
  placeholder?: string;
  /** Rendered when there are no items and nothing is loading. */
  empty?: ReactNode;
}

export function CommandPalette({
  open,
  onClose,
  query,
  onQueryChange,
  groups,
  placeholder = "Search or run an action",
  empty,
}: CommandPaletteProps) {
  const reduce = useReducedMotion();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const baseId = useId();
  const listId = `${baseId}-list`;

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const loading = groups.some((g) => g.loading);
  const [active, setActive] = useState(0);

  // New results: highlight the first one.
  useEffect(() => {
    setActive(0);
  }, [query, flat.length]);

  // Remember the opener, focus the input, restore focus on close.
  useLayoutEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement as HTMLElement | null;
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.clearTimeout(t);
      document.body.style.overflow = overflow;
      const opener = openerRef.current;
      if (opener && document.contains(opener)) opener.focus();
    };
  }, [open]);

  // Keep the highlighted option in view.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const runAt = (i: number) => {
    const item = flat[i];
    if (!item) return;
    onClose();
    item.run();
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const n = flat.length;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (n) setActive((a) => (a + 1) % n);
        break;
      case "ArrowUp":
        e.preventDefault();
        if (n) setActive((a) => (a - 1 + n) % n);
        break;
      case "Home":
        if (n) {
          e.preventDefault();
          setActive(0);
        }
        break;
      case "End":
        if (n) {
          e.preventDefault();
          setActive(n - 1);
        }
        break;
      case "Enter":
        e.preventDefault();
        runAt(active);
        break;
      case "Escape":
        e.preventDefault();
        e.stopPropagation();
        onClose();
        break;
      case "Tab":
        // Focus is trapped: the input is the only tab stop in the palette.
        e.preventDefault();
        inputRef.current?.focus();
        break;
      default:
        break;
    }
  };

  const activeId = flat[active] ? `${baseId}-opt-${active}` : undefined;
  let index = -1;

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <Fragment key="cp">
          <motion.div
            className="cp-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.16 }}
            onClick={onClose}
            aria-hidden
          />
          <div className="cp-wrap">
            <motion.div
              className="cp"
              role="dialog"
              aria-modal="true"
              aria-label="Command palette"
              initial={{ opacity: 0, y: reduce ? 0 : -8, scale: reduce ? 1 : 0.985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: reduce ? 0 : -6, scale: reduce ? 1 : 0.985 }}
              transition={{ duration: reduce ? 0 : 0.2, ease: [0.22, 1, 0.36, 1] }}
              onKeyDown={onKeyDown}
            >
              <div className="cp-field">
                <Search size={18} strokeWidth={1.75} className="cp-field__icon" aria-hidden />
                <input
                  ref={inputRef}
                  className="cp-input"
                  type="text"
                  role="combobox"
                  aria-expanded="true"
                  aria-controls={listId}
                  aria-activedescendant={activeId}
                  aria-autocomplete="list"
                  aria-label="Search"
                  placeholder={placeholder}
                  value={query}
                  onChange={(e) => onQueryChange(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
                <span className="cp-esc" aria-hidden>
                  Esc
                </span>
              </div>

              <div className="cp-list" id={listId} role="listbox" aria-label="Results" ref={listRef}>
                {groups.map((g) =>
                  g.items.length === 0 && !g.loading ? null : (
                    <div key={g.label} role="group" aria-label={g.label}>
                      <p className="st-label cp-group">{g.label}</p>
                      {g.items.map((item) => {
                        index += 1;
                        const i = index;
                        const Icon = item.icon;
                        return (
                          <div
                            key={item.id}
                            id={`${baseId}-opt-${i}`}
                            data-index={i}
                            role="option"
                            aria-selected={i === active}
                            className="cp-item"
                            onMouseMove={() => {
                              if (i !== active) setActive(i);
                            }}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => runAt(i)}
                          >
                            <span className="cp-item__icon" aria-hidden>
                              <Icon size={16} strokeWidth={1.75} />
                            </span>
                            <span className="cp-item__text">
                              <span className="cp-item__label">
                                <Highlight text={item.label} query={query} />
                              </span>
                              {item.sub ? <span className="cp-item__sub">{item.sub}</span> : null}
                            </span>
                            {item.hint ? <span className="cp-item__hint">{item.hint}</span> : null}
                            <CornerDownLeft size={14} strokeWidth={1.75} className="cp-item__enter" aria-hidden />
                          </div>
                        );
                      })}
                      {g.loading
                        ? [0, 1].map((k) => (
                            <div key={`sk-${k}`} className="cp-skel" aria-hidden>
                              <Skeleton block width={32} height={32} radius="var(--radius-md)" />
                              <span className="cp-item__text">
                                <SkeletonLine lineHeight="calc(14px * 1.3)" barHeight={11} width={`${52 - k * 14}%`} />
                                <SkeletonLine lineHeight="calc(12.5px * 1.35)" barHeight={9} width={`${30 - k * 6}%`} />
                              </span>
                            </div>
                          ))
                        : null}
                    </div>
                  ),
                )}
                {flat.length === 0 && !loading ? <div className="cp-empty">{empty}</div> : null}
              </div>

              <div className="cp-foot" aria-hidden>
                <span>
                  <kbd>↑</kbd>
                  <kbd>↓</kbd>
                  to move
                </span>
                <span>
                  <kbd>↵</kbd>
                  to open
                </span>
                <span>
                  <kbd>Esc</kbd>
                  to close
                </span>
              </div>
            </motion.div>
          </div>
        </Fragment>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

/** Marks the first contiguous, case-insensitive match of the query. */
function Highlight({ text, query }: { text: string; query: string }) {
  const q = query.trim();
  if (!q) return <>{text}</>;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark>{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}
